import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import {
  fullloader,
  setProgressType,
  toast,
  updateNavProgressMeter,
  updateProgressMeterResults,
} from '../../../redux/action';
import { getMsgBy_Id, getMessageEventInfo } from '../../../redux/services';
import {
  fetchOrgGroups,
  getOrganizationTopics,
} from '../../../redux/services/groups';
import { selectedOrganizationSelector } from '../../../redux/selector';
import { createIdNameMappingFromData } from '../../../utils';
import { useMessagesDailyV2Query } from '../api';
import { useTableState } from '../tableState';
import { CHANNEL_TILES, getCombinedTileValues } from '../utils/rates';
import {
  MAX_COMBINED_RANGE_DAYS,
  getCombinedDailySeries,
  getCombinedResends,
  getCombinedStart,
  getDayRange,
  getDefaultCombinedRange,
  getMetricsForChannel,
  isOutsideMaxRange,
} from '../utils/graph';
import { getSingleMessageExport } from '../utils/export';
import { Page } from '../AnalyticsV2.styled';
import EngagementGraph from '../MessageDetail/EngagementGraph';
import StatTiles from '../MessageDetail/StatTiles';
import Header from './Header';
import CombinedDrawer from './drawers/CombinedDrawer';

// Messages fetched at the same time for the export.
const EXPORT_BATCH_SIZE = 5;

// The combined stats page for the messages selected on the table: the
// averaged engagement graph by day and the averaged stat tiles.
// tablePath: the message table's URL, for "Back to Messages".
const CombinedStats = ({ tablePath }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const organization = useSelector(selectedOrganizationSelector);
  const { orgId, selected } = useTableState();

  const [channel, setChannel] = useState('all');
  const [metrics, setMetrics] = useState(['open']);
  const [chartType, setChartType] = useState('line');
  // The picked range, or null for the default (first send to last event).
  const [pickedRange, setPickedRange] = useState(null);
  // The dates clicked while the picker is open, to block ranges longer than
  // MAX_COMBINED_RANGE_DAYS (antd's pattern for a range limit).
  const [calendarDates, setCalendarDates] = useState(null);
  // The tile whose drawer is open, or null.
  const [drawerTile, setDrawerTile] = useState(null);

  // Newest first, as in the table and the export.
  const messages = [...selected.values()].sort(
    (a, b) => b.sendTime - a.sendTime
  );
  const ids = messages.map(message => message.id);
  const start = getCombinedStart(messages);

  // The default range asks for MAX_COMBINED_RANGE_DAYS days from the first
  // send; it then ends on the last day with an event.
  const requestRange = pickedRange
    ? getDayRange(pickedRange)
    : { start, days: MAX_COMBINED_RANGE_DAYS };
  const daily = useMessagesDailyV2Query(orgId, ids, requestRange, {
    enabled: start !== null,
  });

  const goBack = () => history.push(tablePath);

  // The selection lives in the table state, so a reload (or a URL opened
  // directly) has none: go to the table instead.
  if (messages.length === 0) return <Redirect to={tablePath} />;

  const dateRange =
    pickedRange ||
    (daily.data ? getDefaultCombinedRange(start, daily.data) : null);
  const pointsRange = dateRange && getDayRange(dateRange);
  const points =
    daily.data && pointsRange
      ? getCombinedDailySeries({
          messages,
          daysById: daily.data,
          channel,
          metrics,
          start: pointsRange.start,
          dayCount: pointsRange.days,
        })
      : [];

  const changeChannel = nextChannel => {
    setChannel(nextChannel);
    setMetrics(getMetricsForChannel(metrics, nextChannel));
  };

  // The last metric on the graph can't be removed (its toggle is disabled).
  const toggleMetric = metric => {
    const next = metrics.includes(metric)
      ? metrics.filter(key => key !== metric)
      : [...metrics, metric];
    setMetrics(CHANNEL_TILES[channel].filter(key => next.includes(key)));
  };

  // One CSV row per message, built as the detail page builds its one row.
  const exportCsv = async () => {
    dispatch(fullloader(true, 'Preparing export...'));
    dispatch(setProgressType('ANALYTICS_CSV'));
    try {
      const [orgGroups, orgTopics] = await Promise.all([
        fetchOrgGroups(orgId),
        getOrganizationTopics(orgId),
      ]);
      // These services don't throw: on failure they return { error: true }.
      if (orgGroups.error || orgTopics.error) {
        throw new Error('A request for the export failed');
      }
      const groups = createIdNameMappingFromData(orgGroups);
      const topics = createIdNameMappingFromData(orgTopics);

      const rows = [];
      for (let index = 0; index < messages.length; index += EXPORT_BATCH_SIZE) {
        const batch = messages.slice(index, index + EXPORT_BATCH_SIZE);
        const batchRows = await Promise.all(
          batch.map(async message => {
            const [fullMessage, eventInfo] = await Promise.all([
              getMsgBy_Id(message._id, true),
              getMessageEventInfo({
                msgId: message.id,
                BmessageId: message.AB?.linkedMessageId,
              }),
            ]);
            // These services don't throw: on failure they return null or
            // undefined.
            if (!fullMessage || !eventInfo) {
              throw new Error('A request for the export failed');
            }
            return getSingleMessageExport({
              organization,
              message: fullMessage,
              emailEvents: eventInfo.emailEvents,
              textEvents: eventInfo.textEvents,
              groups,
              topics,
            }).messages[0];
          })
        );
        rows.push(...batchRows);
        // Below 100 until the file is ready: at 100 the meter offers it.
        dispatch(
          updateNavProgressMeter(
            Math.min(99, Math.round((rows.length / messages.length) * 100))
          )
        );
      }

      dispatch(
        updateProgressMeterResults({
          messages: rows,
          filename: `${organization.acronym} Analytics Export, ${messages.length} messages.csv`,
        })
      );
      dispatch(updateNavProgressMeter(100));
    } catch (e) {
      dispatch(updateNavProgressMeter(0));
      dispatch(
        toast('error', "Couldn't export the messages. Please try again.")
      );
    } finally {
      dispatch(fullloader(false));
    }
  };

  return (
    <Page>
      <Header
        messages={messages}
        dateRange={dateRange}
        // Clearing the picker restores the default range.
        onDateRangeChange={setPickedRange}
        disabledDate={date =>
          isOutsideMaxRange(date, calendarDates, MAX_COMBINED_RANGE_DAYS)
        }
        onCalendarChange={setCalendarDates}
        onOpenChange={isOpen => setCalendarDates(isOpen ? [null, null] : null)}
        onBack={goBack}
        onExport={exportCsv}
      />
      <EngagementGraph
        channel={channel}
        metrics={metrics}
        chartType={chartType}
        onChartTypeChange={setChartType}
        points={points}
        isLoading={daily.isLoading}
        isError={daily.isError}
        // Every selected message is scheduled or never sent anything.
        noDataText={start === null ? 'No data yet' : null}
        resends={getCombinedResends(messages)}
      />
      <StatTiles
        tiles={getCombinedTileValues(messages, channel)}
        isSmallValue={false}
        channel={channel}
        onChannelChange={changeChannel}
        metrics={metrics}
        onToggleMetric={toggleMetric}
        onTileClick={setDrawerTile}
      />
      {drawerTile && (
        <CombinedDrawer
          orgId={orgId}
          ids={ids}
          tileKey={drawerTile}
          channel={channel}
          onClose={() => setDrawerTile(null)}
        />
      )}
    </Page>
  );
};

export default CombinedStats;
